import { type ChangeEvent, type Ref } from "react";

import { Box } from "ui/box";
import { TextField, type TextFieldProps } from "ui/text-field";

import { isHexColor } from "containers/admin/clients/client/theme-configuration-page/helpers/color-shades";

import {
  NATIVE_PICKER_SX,
  PICKER_SX,
  Swatch,
} from "containers/admin/clients/client/theme-configuration-page/components/color-field/color-field.styles";

import Icons from "assets";

export type ColorFieldProps = Omit<
  TextFieldProps,
  "onChange" | "ref" | "startAdornment" | "startIcon" | "value"
> & {
  value?: string;
  label: string;
  /** Called with the normalised value: upper-cased, with a single leading `#`, or `""` when cleared. */
  onChange: (value: string) => void;
  ref?: Ref<HTMLInputElement>;
};

/** The native picker cannot hold an empty or partial value, so it shows black until the text is a full hex. */
const PICKER_FALLBACK = "#000000";

const normalizeHex = (raw: string) => (raw ? `#${raw.replace(/#/g, "").toUpperCase()}` : "");

export const ColorField = ({
  value = "",
  label,
  disabled,
  onChange,
  slotProps,
  ...rest
}: ColorFieldProps) => {
  const hasColor = isHexColor(value);

  const handleTextChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange(normalizeHex(event.target.value));

  const handlePickerChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange(event.target.value.toUpperCase());

  return (
    <TextField
      {...rest}
      label={label}
      value={value}
      disabled={disabled}
      onChange={handleTextChange}
      placeholder="Type hex code..."
      slotProps={{ ...slotProps, input: { maxLength: 7, ...slotProps?.input } }}
      startAdornment={
        <Box sx={PICKER_SX}>
          {hasColor ? <Swatch swatchColor={value} /> : <Icons.ColorEmpty aria-hidden />}
          <Box
            component="input"
            type="color"
            aria-label={`Pick ${label} color`}
            value={hasColor ? value.toLowerCase() : PICKER_FALLBACK}
            disabled={disabled}
            onChange={handlePickerChange}
            sx={NATIVE_PICKER_SX}
          />
        </Box>
      }
    />
  );
};
